// MACHINERY V2 (Stroje) — V3 table: identity-side cells (M2.13).
//
// STAV · PRIPOJ. · STROJ · LOKÁCIA. The data-side cells (AKTUÁLNE, 30 DNÍ,
// OTV. SR) live in fleetTableDataCells.tsx — split for the 300-line cap.
//
// Reference impl: design_handoff_v3v4v5_mobile/desktop/machinery-v3-table.jsx +
// desktop/README.md §V3; rendered truth
// desktop/screenshots/v3-table--{refined-claude,machineid-native}.png (both
// viewed before this file was written, per the view-before-code rule).
//
// EVERY STATUS-SHAPED VALUE COMES FROM deriveCardPresentation(), NEVER FROM RAW
// TELEMETRY — the M2.1 contract, exactly as FleetCard and FleetRow consume it.
// The handoff prototype re-tests `activeAlarms.length` / `conn !== null` inline;
// porting that literally would re-open the split brain M2.1 closed, so the
// presentation object is passed in and these cells only read its flags.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { useGalleryImages } from "../../../../../shared/components/gallery";
import {
  getMachineDisplayName,
  getMachineName,
} from "../../../../../shared/utils/machineUtils";
import { Machine } from "../../../models/models";
import { CapabilityBadges } from "../../readiness/CapabilityBadge";
import { MachineryV2ReadinessPresentation } from "../../readiness/readinessPresentation";
import ConnDot from "../ConnDot";
import StatusBadge, { AwaitingBadge } from "../StatusBadge";
import { MachineryV2CardPresentation, MachineryV2Telemetry } from "../cardStates";
import { MachinePhoto, NewBadge, PinGlyph } from "../fleetCardAtoms";

/** The dense "nothing here" mark used across the table. */
export const Dash: React.FC<{ size?: number }> = ({ size = 11 }) => (
  <span style={{ fontFamily: "var(--font-mono)", fontSize: size, color: "var(--faint)" }}>—</span>
);

export interface StatusCellProps {
  presentation: MachineryV2CardPresentation;
  telemetry: MachineryV2Telemetry;
}

/**
 * STAV (130) — chip badge + the mono freshness sub-line, plus the ALARM CORNER
 * TRIANGLE pinned to the cell's top-left.
 *
 * The triangle is absolutely positioned inside the <td> (the row sets no
 * transform, and the td is `position: relative` here), so it costs ZERO column
 * width — the brief's "steals no column" rule. Its tooltip is the native
 * `title`, i.e. text-only, which is what the tooltip ruling asks for and what
 * ConnDot.tsx:71-75 already ships; a Radix tooltip would need the portal
 * scope-class dance for a two-word string.
 *
 * The sub-line renders ONLY what the wire carries. Today the gateway summary
 * sends neither `since` nor `heartbeat` (fleetTelemetry.ts header, personally
 * re-read: "no since/elapsed, no heartbeat age"), so it is usually absent. That
 * is the honest rendering — it lights up unchanged when §6 lands.
 */
export const StatusCell: React.FC<StatusCellProps> = ({ presentation: p, telemetry: t }) => {
  const { translate } = useTranslation("myMachinery");
  const alarm = p.showAlarmStrip && t.alarms.length > 0 ? t.alarms[0] : null;
  const since = t.since ? translate("myMachinery:v2.sincePrefix") + " " + t.since : null;
  const sub = since || t.heartbeat || null;

  return (
    <>
      {alarm && (
        <span
          title={alarm.code + ": " + alarm.message}
          style={{
            position: "absolute", top: 0, left: 0, width: 0, height: 0,
            borderTop: "16px solid var(--red)", borderRight: "16px solid transparent",
          }}
        >
          <span
            style={{
              position: "absolute", top: -15, left: 2, color: "#fff", fontSize: 9,
              fontFamily: "var(--font-mono)", fontWeight: 700, lineHeight: 1,
            }}
          >
            !
          </span>
        </span>
      )}
      {/* `showStatusRow` alone is NOT enough to promise a visible chip. A
          `stale` machine has showStatusRow true but carries NO status —
          "TelemetryStale" maps to `{ conn: "stale", status: null }`
          (fleetTelemetry.ts:100) and deriveCardPresentation passes `t.status`
          straight through for that state (cardStates.ts:268-269) — so
          StatusBadge hits its `if (!status) return null` (StatusBadge.tsx:72)
          and the cell renders NOTHING. In a dense table where every other row
          shows a chip, a blank STAV cell reads as a rendering failure, so the
          dash fallback covers that case too. TABLE-ONLY: the shared card/row
          branch is deliberately left alone this wave — it is shipped and
          owner-tested, and there the badge sits beside an inline conn dot, so
          an absent chip does not read as broken. */}
      {p.showStatusRow && (p.showAwaitingBadge || p.badgeStatus !== null) ? (
        p.showAwaitingBadge ? (
          <AwaitingBadge />
        ) : (
          <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="chip" />
        )
      ) : (
        <span
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)",
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}
        >
          —
        </span>
      )}
      {sub && (
        <div
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)",
            marginTop: 4, whiteSpace: "nowrap",
          }}
        >
          {sub}
        </div>
      )}
    </>
  );
};

/** PRIPOJ. (38) — the 8px dot, hover reveals the heartbeat (ConnDot's own
 *  native title). `dotConn` is null for unconnected AND awaiting, and the
 *  artboard's rule for those rows is a bare mono dash with no dot. */
export const ConnCell: React.FC<StatusCellProps> = ({ presentation: p, telemetry: t }) =>
  p.dotConn ? <ConnDot state={p.dotConn} lastHeartbeat={t.heartbeat} /> : <Dash />;

export interface MachineCellProps {
  machine: Machine;
  readiness: MachineryV2ReadinessPresentation | null;
  /** Carried for parity with FleetCard / FleetRow: a machine just added stays
   *  findable in whichever view the user happens to be in. The artboard has no
   *  slot for it, so it sits inline after the name rather than inventing one. */
  isNew: boolean;
  onOpenReadiness: () => void;
}

/**
 * STROJ (flex) — 56x42 silhouette with the capability badges overlaid, bold
 * 13.5 name, 11.5 muted meta (type · S/N mono · system).
 *
 * The badges are the M2.9 kit unchanged. Their click AND key guards live inside
 * CapabilityBadge (CapabilityBadge.tsx:170-192 — re-read while writing this
 * cell), which is why no wrapper guard is added here: a per-consumer guard is
 * the one the next consumer forgets.
 */
export const MachineCell: React.FC<MachineCellProps> = ({
  machine, readiness, isNew, onOpenReadiness,
}) => {
  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });
  const meta = getMachineName(machine) || machine.type?.name || "";
  const system = machine.controlSystemBrand?.name || machine.system || "";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <div style={{ position: "relative", width: 56, height: 42, flexShrink: 0 }}>
        <MachinePhoto
          src={images.length > 0 ? images[0].src : null}
          alt={getMachineDisplayName(machine)}
          grayscale={!!machine.isDecommissioned}
          height={42}
          width={56}
        />
        {readiness && (
          <div style={{ position: "absolute", top: 2, left: 2, display: "flex", gap: 3 }}>
            <CapabilityBadges
              presentation={readiness}
              size={14}
              onOpenDetails={onOpenReadiness}
            />
          </div>
        )}
      </div>
      {/* maxWidth, not a table column width: the table is `auto` layout (see
          FleetTable's header), so a long name would otherwise stretch the whole
          table instead of ellipsing. */}
      <div style={{ minWidth: 0, maxWidth: 300 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            style={{
              fontWeight: 600, fontSize: 13.5, color: "var(--ink)", lineHeight: 1.25,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {getMachineDisplayName(machine)}
          </span>
          {isNew && <NewBadge />}
        </div>
        <div
          style={{
            fontSize: 11.5, color: "var(--muted)", marginTop: 1, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {/* Separators are conditional: a custom machine with no type and no
              control system must not render a stray "· · ". */}
          {meta ? meta + " · " : ""}
          <span style={{ fontFamily: "var(--font-mono)", color: "var(--faint)" }}>
            S/N {machine.serialNumber || "—"}
          </span>
          {system ? " · " + system : ""}
        </div>
      </div>
    </div>
  );
};

/** LOKÁCIA (170) — závod line + hala sub-line, as the artboard splits them.
 *  The v2 row renders the same pair inline as "parent › child"; this is the
 *  same two values, stacked, not a second location model. */
export const LocationCell: React.FC<{ machine: Machine }> = ({ machine }) => {
  const { translate } = useTranslation("myMachinery");
  if (!machine.locationName) {
    return (
      <span style={{ color: "var(--faint)", fontStyle: "italic", fontSize: 12 }}>
        {translate("myMachinery:locationNotSet")}
      </span>
    );
  }
  const primary = machine.parentLocationName || machine.locationName;
  const sub = machine.parentLocationName ? machine.locationName : null;

  return (
    <>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 5, fontSize: 12.5,
          color: "var(--ink-2)", minWidth: 0,
        }}
      >
        <span style={{ color: "var(--mod-accent)", display: "inline-flex", flexShrink: 0 }}>
          <PinGlyph />
        </span>
        <span
          style={{
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: 140,
          }}
        >
          {primary}
        </span>
      </div>
      {sub && (
        <div
          style={{
            fontSize: 11.5, color: "var(--muted)", marginTop: 2, paddingLeft: 16,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: 156,
          }}
        >
          {sub}
        </div>
      )}
    </>
  );
};
